import React, { useState, useEffect } from "react";
import Button from "@components/Button";
import CustomInput, { CustomSelectInput } from "@components/CustomInput/CustomInput";
import TableComponent from "@components/TableComponent/TableComponent";
import { Home } from "@mui/icons-material";
import { useNavigate } from "react-router";
import styles from "./ManagePostPayPage.module.css";
import { useSelector } from "react-redux";
import usePostPay from "@hooks/usePostPay";

const ManagePostPayPage = () => {
  const navigate = useNavigate();
  const { handleViewUsers } = usePostPay();
  const { doctorData } = useSelector((state) => state.postpay);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [step, setStep] = useState("manage");
  const [searchFilters, setSearchFilters] = useState({
    doctorId: "",
    myReferenceNumber: "",
    actionEnabled: "",
    viewAccess: "",
  });

  const handleSearchInput = ({ target }) => {
    const { name, value } = target;
    setSearchFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleNavigateViewUserPage = (doctor) => {
    if(step === "manage"){
      navigate(`/managepostpay/viewuser/${doctor.myReferenceNumber}`, 
        {
          state:{
            doctor:doctor
          }
      });
    }
    else{
      navigate("/managepostpay/postpayhistory", 
        {
          state:{
            doctor:doctor
          }
      });
    }
  };

  const createData = (doctorId, refNo, actionEnabled, viewAccess, doctor) => {
    return {
      doctorId,
      myReferenceNumber: refNo,
      isEnabled: actionEnabled? "Yes" : "No",
      isViewable: viewAccess? "Yes" : "No",
      view: (
        <Button
          text="View >"
          onClick={() => handleNavigateViewUserPage(doctor)}
        />
      ),
    };
  };

  const rows = doctorData?.data?.map((doctor) =>
    createData(
      doctor._id, 
      doctor.myReferenceNumber, 
      doctor.permissions?.visionPostpaid?.isEnabled ?? false, 
      doctor.permissions?.visionPostpaid?.isViewable ?? false,
      doctor
    )
  );

  const columns = [
    { id: "doctorId", label: "DoctorId", minWidth: 150 },
    { id: "myReferenceNumber", label: "Reference No", minWidth: 150 },
    { id: "isViewable", label: "View Access", minWidth: 150 },
    { id: "isEnabled", label: "Action Enabled", minWidth: 150 },
    {
      id: "view",
      label: "View",
      minWidth: 100,
      align: "left"
    },
  ];

  const filteredRows = rows?.filter((row) =>
    Object.keys(searchFilters).every((key) =>
      searchFilters[key] ? row[key]?.toString().toLowerCase().includes(searchFilters[key].toLowerCase()) : true
    )
  );

  const viewAccessOptions = [
    { option: "Yes", value: "Yes" },
    { option: "No", value: "No" },
  ];

  const actionEnabledOptions = [
    { option: "Yes", value: "Yes" },
    { option: "No", value: "No" },
  ];

  const handleNavigateDashboard = () => {
    navigate("/");
  };

  const handleNavigateUpgradeAccessPage = () => {
    navigate("/managepostpay/upgradeaccess");
  };

  const handleNavigateHistory = (doctor) => {
    // navigate("/managepostpay/postpayhistory");
    if(step === "manage"){
    setStep("history");
    }
    else{
    setStep("manage");
  }
  };

  const tableHeads = [
    {
      component: (
        <CustomInput
          align={"left"}
          className={styles.Input}
          showSearchIcon
          name="doctorId"
          value={searchFilters.doctorId}
          onChange={handleSearchInput}
        />
      ),
    },
    {
      component: (
        <CustomInput
          align={"left"}
          className={styles.Input}
          showSearchIcon
          name="myReferenceNumber"
          value={searchFilters.myReferenceNumber}
          onChange={handleSearchInput}
        />
      ),
    },
    {
      component: (
        <CustomSelectInput
          className={styles.Input}
          selectOptions={viewAccessOptions}
          defaultOption={"View Access"}
          value={searchFilters.viewAccess}
          onChange={handleSearchInput}
          name="viewAccess"
        />
      )
    },
    {
      component: (
        <CustomSelectInput
          className={styles.Input}
          selectOptions={actionEnabledOptions}
          defaultOption={"Action Enabled"}
          value={searchFilters.actionEnabled}
          onChange={handleSearchInput}
          name="actionEnabled"
        />
      ),
    }
  ];

  const searchFunction = () => {
    let isViewAccess = searchFilters.viewAccess==="Yes" ? true : false;
    let postPayEnabled = searchFilters.actionEnabled==="Yes" ? true : false;
    if(searchFilters.viewAccess === "") isViewAccess = true;
    if(searchFilters.actionEnabled==="") postPayEnabled = true;
    
    handleViewUsers({ 
      postPayView: isViewAccess, 
      postPayEnabled: postPayEnabled,
      myReferenceNumber: searchFilters.myReferenceNumber,
      doctorId: searchFilters.doctorId, 
      page: page + 1, 
      limit: rowsPerPage });
  };

  useEffect(() => {
    let isViewAccess = searchFilters.viewAccess === "Yes" ? true : false;
    let isActionEnabled = searchFilters.actionEnabled === "Yes" ? true : false;
    if(searchFilters.viewAccess === "") isViewAccess = true;
    if(searchFilters.actionEnabled==="") isActionEnabled = true;
  
    handleViewUsers({ 
      postPayView: isViewAccess, 
      postPayEnabled: isActionEnabled, 
      page: page + 1, 
      limit: rowsPerPage 
    });
  }, [page, rowsPerPage]);
  

  return (
    <>
      <div className={styles.MenuWrapper}>
          <div className={styles.NavigationWrapper}>
            <Home className={styles.HomeIcon} onClick={handleNavigateDashboard} />{" "}
            <div>
              <span
               className={styles.DashboardText}
               onClick={handleNavigateDashboard}
              >
              Dashboard
              </span>
              <span className={styles.RouteText}>/PostPay Panel</span>
            </div>
          </div>
      <div className={styles.ActionItemsWrapper}>
          <Button
            className={styles.BtnClass}
            text={step === "history"? "View User" : "View History"}
            btnType={"secondary"}
            onClick={() => handleNavigateHistory()}
          />
          <Button
            className={styles.BtnClass}
            text={"Upgrade Access"}
            btnType={"secondary"}
            onClick={handleNavigateUpgradeAccessPage}
          />
      </div>
      </div>
      <TableComponent
        tableHeads={tableHeads}
        columns={columns}
        rows={rows}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        page={page}
        setPage={setPage}
        totalRecords={doctorData?.total_record ?? 0}
        searchFunction={searchFunction}
     />
    </>
  )
};

export default ManagePostPayPage;
